<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, shallowRef, useTemplateRef } from 'vue'
import wavingHandCursorSrc from '@/assets/cursors/waving-hand-cursor.gif'
import brandLogoSrc from '@/assets/images/app-brand-logo.png'
import logoAnimationSrc from '@/assets/videos/logo-animation.mp4'

defineOptions({
  name: 'AppBrandLogo',
})

const MAX_TILT_X = 14
const MAX_TILT_Y = 18
const MAX_MAGNET_X = 4
const MAX_MAGNET_Y = 4
const NEUTRAL_TILT_STATE = {
  cursorX: '50%',
  cursorY: '50%',
  glowX: '50%',
  glowY: '42%',
  magnetX: '0px',
  magnetY: '0px',
  rotateX: '0deg',
  rotateY: '0deg',
}

interface TiltState {
  cursorX: string
  cursorY: string
  glowX: string
  glowY: string
  magnetX: string
  magnetY: string
  rotateX: string
  rotateY: string
}

const rootElement = useTemplateRef<HTMLElement>('rootElement')
const videoElement = useTemplateRef<HTMLVideoElement>('videoElement')
const isHovering = shallowRef(false)
const isVideoReady = shallowRef(false)
const isVideoPlaying = shallowRef(false)
const prefersReducedMotion = shallowRef(false)

const isVideoVisible = computed(
  () =>
    isHovering.value &&
    isVideoReady.value &&
    isVideoPlaying.value &&
    !prefersReducedMotion.value,
)
const isCustomCursorVisible = computed(() => isHovering.value && !prefersReducedMotion.value)
const shouldRenderVideoElement = computed(() => isHovering.value && !prefersReducedMotion.value)

let pendingTiltState: TiltState | null = null
let tiltAnimationFrame = 0
let reducedMotionQuery: MediaQueryList | null = null

function formatDegree(value: number) {
  return `${value.toFixed(2)}deg`
}

function formatPercent(value: number) {
  return `${value.toFixed(2)}%`
}

function formatPixel(value: number) {
  return `${value.toFixed(2)}px`
}

function applyTiltState(state: TiltState) {
  const root = rootElement.value

  if (!root) {
    return
  }

  root.style.setProperty('--brand-logo-rotate-x', state.rotateX)
  root.style.setProperty('--brand-logo-rotate-y', state.rotateY)
  root.style.setProperty('--brand-logo-magnet-x', state.magnetX)
  root.style.setProperty('--brand-logo-magnet-y', state.magnetY)
  root.style.setProperty('--brand-logo-cursor-x', state.cursorX)
  root.style.setProperty('--brand-logo-cursor-y', state.cursorY)
  root.style.setProperty('--brand-logo-glow-x', state.glowX)
  root.style.setProperty('--brand-logo-glow-y', state.glowY)
}

function flushTiltState() {
  tiltAnimationFrame = 0

  if (!pendingTiltState) {
    return
  }

  applyTiltState(pendingTiltState)
  pendingTiltState = null
}

function scheduleTiltState(state: TiltState) {
  pendingTiltState = state

  if (typeof window === 'undefined') {
    flushTiltState()
    return
  }

  if (tiltAnimationFrame) {
    return
  }

  tiltAnimationFrame = window.requestAnimationFrame(flushTiltState)
}

function resetTiltState() {
  scheduleTiltState(NEUTRAL_TILT_STATE)
}

function isTouchPointer(event: MouseEvent | PointerEvent) {
  return 'pointerType' in event && event.pointerType === 'touch'
}

function getPointerTiltState(event: MouseEvent | PointerEvent): TiltState | null {
  const root = rootElement.value

  if (!root) {
    return null
  }

  const rect = root.getBoundingClientRect()

  if (rect.width <= 0 || rect.height <= 0) {
    return null
  }

  const x = (event.clientX - rect.left) / rect.width
  const y = (event.clientY - rect.top) / rect.height
  const localX = event.clientX - rect.left
  const localY = event.clientY - rect.top
  const centeredX = x - 0.5
  const centeredY = y - 0.5

  return {
    cursorX: formatPixel(localX),
    cursorY: formatPixel(localY),
    glowX: formatPercent(x * 100),
    glowY: formatPercent(y * 100),
    magnetX: formatPixel(centeredX * MAX_MAGNET_X),
    magnetY: formatPixel(centeredY * MAX_MAGNET_Y),
    rotateX: formatDegree(centeredY * -MAX_TILT_X),
    rotateY: formatDegree(centeredX * MAX_TILT_Y),
  }
}

function markVideoReady() {
  isVideoReady.value = true
}

function markVideoPlaying() {
  isVideoReady.value = true
  isVideoPlaying.value = true
}

function pauseAndResetVideo() {
  const video = videoElement.value

  isVideoPlaying.value = false

  if (!video) {
    return
  }

  video.pause()

  try {
    video.currentTime = 0
  } catch {
    // Some browsers disallow seeking until metadata has loaded.
  }
}

async function playLogoAnimation() {
  await nextTick()

  const video = videoElement.value

  if (!video || prefersReducedMotion.value) {
    return
  }

  video.muted = true
  video.playsInline = true

  try {
    video.currentTime = 0
  } catch {
    // Play can still start at the beginning once metadata is available.
  }

  try {
    await video.play()

    if (videoElement.value !== video || !isHovering.value) {
      video.pause()
      return
    }

    isVideoPlaying.value = true
  } catch {
    isVideoPlaying.value = false
  }
}

function handleHoverEnter(event: MouseEvent | PointerEvent) {
  if (isHovering.value) {
    handleHoverMove(event)
    return
  }

  isHovering.value = true
  handleHoverMove(event)
  void playLogoAnimation()
}

function handleHoverMove(event: MouseEvent | PointerEvent) {
  if (prefersReducedMotion.value || isTouchPointer(event)) {
    return
  }

  const tiltState = getPointerTiltState(event)

  if (tiltState) {
    scheduleTiltState(tiltState)
  }
}

function handleHoverLeave() {
  if (!isHovering.value) {
    return
  }

  isHovering.value = false
  pauseAndResetVideo()
  resetTiltState()
}

function syncReducedMotion(event?: MediaQueryList | MediaQueryListEvent) {
  prefersReducedMotion.value =
    typeof event?.matches === 'boolean' ? event.matches : Boolean(reducedMotionQuery?.matches)

  if (!prefersReducedMotion.value) {
    return
  }

  isHovering.value = false
  pauseAndResetVideo()
  resetTiltState()
}

onMounted(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return
  }

  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  syncReducedMotion(reducedMotionQuery)

  if (typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', syncReducedMotion)
    return
  }

  reducedMotionQuery.addListener?.(syncReducedMotion)
})

onBeforeUnmount(() => {
  pauseAndResetVideo()

  if (tiltAnimationFrame && typeof window !== 'undefined') {
    window.cancelAnimationFrame(tiltAnimationFrame)
  }

  if (typeof reducedMotionQuery?.removeEventListener === 'function') {
    reducedMotionQuery.removeEventListener('change', syncReducedMotion)
    return
  }

  reducedMotionQuery?.removeListener?.(syncReducedMotion)
})
</script>

<template>
  <span
    ref="rootElement"
    class="app-brand-logo"
    :class="{
      'is-hovering': isHovering,
      'is-video-visible': isVideoVisible,
    }"
    aria-hidden="true"
    @mouseenter="handleHoverEnter"
    @mousemove="handleHoverMove"
    @mouseleave="handleHoverLeave"
    @pointerenter="handleHoverEnter"
    @pointermove="handleHoverMove"
    @pointerleave="handleHoverLeave"
    @pointercancel="handleHoverLeave"
  >
    <span class="app-brand-logo__plane">
      <img class="app-brand-logo__image" :src="brandLogoSrc" alt="" draggable="false" />
      <video
        v-if="shouldRenderVideoElement"
        ref="videoElement"
        class="app-brand-logo__video"
        :src="logoAnimationSrc"
        aria-hidden="true"
        controlslist="nodownload nofullscreen noplaybackrate noremoteplayback"
        disablepictureinpicture
        disableremoteplayback
        muted
        loop
        playsinline
        preload="metadata"
        tabindex="-1"
        @loadeddata="markVideoReady"
        @canplay="markVideoReady"
        @playing="markVideoPlaying"
      />
      <span class="app-brand-logo__sheen" />
    </span>
    <img
      v-if="isCustomCursorVisible"
      class="app-brand-logo__cursor"
      :src="wavingHandCursorSrc"
      alt=""
      aria-hidden="true"
      draggable="false"
    />
  </span>
</template>

<style scoped>
.app-brand-logo {
  --brand-logo-cursor-x: 50%;
  --brand-logo-cursor-y: 50%;
  --brand-logo-cursor-anchor-x: 6px;
  --brand-logo-cursor-anchor-y: 6px;
  --brand-logo-cursor-size: 24px;
  --brand-logo-glow-x: 50%;
  --brand-logo-glow-y: 42%;
  --brand-logo-lift: 0px;
  --brand-logo-magnet-x: 0px;
  --brand-logo-magnet-y: 0px;
  --brand-logo-rotate-x: 0deg;
  --brand-logo-rotate-y: 0deg;
  --brand-logo-scale: 1;

  display: block;
  position: relative;
  width: 32px;
  height: 32px;
  cursor: pointer;
  overflow: visible;
  isolation: isolate;
  perspective: 360px;
  transform: translateZ(0);
}

.app-brand-logo__plane {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  background: var(--profile-image-bg);
  transform: translate3d(
      var(--brand-logo-magnet-x),
      calc(var(--brand-logo-magnet-y) + var(--brand-logo-lift)),
      0
    )
    rotateX(var(--brand-logo-rotate-x)) rotateY(var(--brand-logo-rotate-y))
    scale(var(--brand-logo-scale));
  transform-origin: center center;
  transform-style: preserve-3d;
  transition: transform var(--app-interaction-duration) var(--app-interaction-ease);
  will-change: transform;
}

.app-brand-logo:hover .app-brand-logo__plane,
.app-brand-logo.is-hovering .app-brand-logo__plane {
  --brand-logo-lift: -4px;
  --brand-logo-scale: 1.16;

  transition-duration: 90ms;
  transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
}

.app-brand-logo__image,
.app-brand-logo__video,
.app-brand-logo__sheen {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.app-brand-logo__image,
.app-brand-logo__video {
  object-fit: cover;
  backface-visibility: hidden;
}

.app-brand-logo__image {
  z-index: 0;
}

.app-brand-logo__video {
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s var(--app-interaction-ease);
}

.app-brand-logo.is-video-visible .app-brand-logo__video {
  opacity: 1;
}

.app-brand-logo__sheen {
  z-index: 2;
  opacity: 0;
  pointer-events: none;
  background: radial-gradient(
    circle at var(--brand-logo-glow-x) var(--brand-logo-glow-y),
    rgb(255 255 255 / 0.58),
    rgb(255 255 255 / 0) 52%
  );
  mix-blend-mode: screen;
  transform: translateZ(16px);
  transition: opacity var(--app-interaction-duration) var(--app-interaction-ease);
}

.app-brand-logo:hover .app-brand-logo__sheen,
.app-brand-logo.is-hovering .app-brand-logo__sheen {
  opacity: 0.44;
}

.app-brand-logo__cursor {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: var(--brand-logo-cursor-size);
  height: var(--brand-logo-cursor-size);
  pointer-events: none;
  transform: translate3d(
      calc(var(--brand-logo-cursor-x) - var(--brand-logo-cursor-anchor-x)),
      calc(var(--brand-logo-cursor-y) - var(--brand-logo-cursor-anchor-y)),
      0
    )
    rotate(-10deg);
  transform-origin: 35% 35%;
  backface-visibility: hidden;
  will-change: transform;
}

@media (hover: hover) and (pointer: fine) {
  .app-brand-logo.is-hovering {
    cursor: none;
  }
}

@media (hover: none), (pointer: coarse) {
  .app-brand-logo,
  .app-brand-logo__plane {
    transform: none;
  }

  .app-brand-logo:hover .app-brand-logo__plane,
  .app-brand-logo.is-hovering .app-brand-logo__plane {
    --brand-logo-lift: 0px;
    --brand-logo-scale: 1;
  }

  .app-brand-logo__sheen {
    display: none;
  }

  .app-brand-logo__cursor {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-brand-logo__plane,
  .app-brand-logo__video,
  .app-brand-logo__sheen {
    transition: none;
    transform: none;
  }

  .app-brand-logo__video,
  .app-brand-logo__sheen,
  .app-brand-logo__cursor {
    display: none;
  }

  .app-brand-logo.is-hovering {
    cursor: pointer;
  }
}
</style>
